import React, { useState, useEffect, useRef } from 'react';
import { API_BASE_URL, fetchWithCSRF } from '../config';
import './SchoolAutocomplete.css';

function SchoolAutocomplete({ selectedSchools, onSchoolsChange, majors = [], studentProfile = null }) {
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [similarSchools, setSimilarSchools] = useState([]);
  const [loadingSimilar, setLoadingSimilar] = useState(false);
  const [aiRecommendations, setAiRecommendations] = useState([]);
  const [loadingAiRecs, setLoadingAiRecs] = useState(false);
  const [aiRecsError, setAiRecsError] = useState(false);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const aiRecsFetchedRef = useRef(false);

  // Debounce timer
  const debounceTimerRef = useRef(null);

  // Handle clicks outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch AI recommendations after the first school is selected
  useEffect(() => {
    if (selectedSchools.length === 1 && studentProfile && studentProfile.gpa && !aiRecsFetchedRef.current) {
      aiRecsFetchedRef.current = true;
      fetchAiRecommendations();
    }
  }, [selectedSchools.length, studentProfile]);

  const fetchAiRecommendations = async () => {
    if (!studentProfile || !studentProfile.gpa) return;

    setLoadingAiRecs(true);
    setAiRecsError(false);
    try {
      const response = await fetchWithCSRF(
        `${API_BASE_URL}/api/universities/ai/recommend-schools/`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ student_profile: studentProfile }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        // Filter out already selected schools
        const filtered = (data.recommendations || []).filter(
          school => !selectedSchools.some(s => s.id === school.id)
        );
        setAiRecommendations(filtered);
      } else {
        setAiRecsError(true);
      }
    } catch (error) {
      console.error('Error fetching AI recommendations:', error);
      setAiRecsError(true);
    } finally {
      setLoadingAiRecs(false);
    }
  };

  // Fetch autocomplete suggestions
  const fetchSuggestions = async (query) => {
    if (!query || query.length < 2) {
      setSuggestions([]);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/universities/autocomplete/?q=${encodeURIComponent(query)}`);
      if (response.ok) {
        const data = await response.json();
        setSuggestions(data);
        setShowDropdown(true);
      }
    } catch (error) {
      console.error('Error fetching suggestions:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch similar schools when a school is added
  const fetchSimilarSchools = async (schoolId) => {
    setLoadingSimilar(true);
    try {
      // Build URL with majors if provided
      let url = `${API_BASE_URL}/api/universities/similar/${schoolId}/`;
      if (majors && majors.length > 0) {
        const majorsParam = majors.join(',');
        url += `?majors=${encodeURIComponent(majorsParam)}`;
      }

      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        // Filter out already selected schools
        const filtered = data.filter(
          school => !selectedSchools.some(s => s.id === school.id)
        );
        setSimilarSchools(filtered);
      }
    } catch (error) {
      console.error('Error fetching similar schools:', error);
    } finally {
      setLoadingSimilar(false);
    }
  };

  // Handle input change with debounce
  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);

    // Clear previous timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer
    debounceTimerRef.current = setTimeout(() => {
      fetchSuggestions(value);
    }, 300); // 300ms debounce
  };

  // Add school to selected list
  const addSchool = (school) => {
    // Check if already selected
    if (selectedSchools.some(s => s.id === school.id)) {
      return;
    }

    const updatedSchools = [...selectedSchools, school];
    onSchoolsChange(updatedSchools);
    setInputValue('');
    setSuggestions([]);
    setShowDropdown(false);

    // Remove from AI recommendations if present
    setAiRecommendations(prev => prev.filter(s => s.id !== school.id));

    // Fetch similar schools for the first school added (only if no AI profile available)
    if (selectedSchools.length === 0 && !studentProfile?.gpa) {
      fetchSimilarSchools(school.id);
    }
  };

  // Remove school from selected list
  const removeSchool = (schoolId) => {
    const updatedSchools = selectedSchools.filter(s => s.id !== schoolId);
    onSchoolsChange(updatedSchools);

    // Clear similar schools if no schools left
    if (updatedSchools.length === 0) {
      setSimilarSchools([]);
    }
  };

  // Add similar school
  const addSimilarSchool = (school) => {
    addSchool(school);
    // Remove from similar schools list
    setSimilarSchools(prev => prev.filter(s => s.id !== school.id));
  };

  // Add AI recommended school
  const addAiRecommendation = (school) => {
    addSchool(school);
  };

  return (
    <div className="school-autocomplete-container">
      {/* AI Recommendations */}
      {loadingAiRecs && (
        <div className="ai-recommendations-loading">
          <div className="loading-spinner"></div>
          <p className="loading-text">Finding schools that match your profile...</p>
        </div>
      )}

      {!loadingAiRecs && aiRecommendations.length > 0 && (
        <div className="ai-recommendations-section">
          <p className="ai-recommendations-label">Schools that match your profile:</p>
          <div className="suggestion-chips">
            {aiRecommendations.map((school) => (
              <button
                key={school.id}
                type="button"
                className="suggestion-chip ai-suggestion-chip"
                onClick={() => addAiRecommendation(school)}
                title={`${school.city}, ${school.state}`}
              >
                + {school.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input field with dropdown */}
      <div className="autocomplete-input-wrapper">
        <input
          ref={inputRef}
          type="text"
          className="form-input autocomplete-input"
          placeholder="Start typing a college name... (e.g., UCLA, Harvard)"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => {
            if (suggestions.length > 0) {
              setShowDropdown(true);
            }
          }}
        />
        {loading && <div className="autocomplete-spinner">&#x23F3;</div>}

        {/* Dropdown suggestions - positioned relative to input wrapper */}
        {showDropdown && suggestions.length > 0 && (
          <div ref={dropdownRef} className="autocomplete-dropdown">
            {suggestions.map((school) => (
              <div
                key={school.id}
                className="autocomplete-item"
                onClick={() => addSchool(school)}
              >
                <div className="school-name-autocomplete">{school.name}</div>
                {school.city && school.state && (
                  <div className="school-location-autocomplete">
                    {school.city}, {school.state}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Selected schools */}
      {selectedSchools.length > 0 && (
        <div className="selected-schools">
          <p className="selected-schools-label">Your selected schools:</p>
          <div className="selected-schools-list">
            {selectedSchools.map((school) => (
              <div key={school.id} className="selected-school-chip">
                <span>{school.name}</span>
                <button
                  type="button"
                  className="remove-school-btn"
                  onClick={() => removeSchool(school.id)}
                  aria-label="Remove school"
                >
                  &times;
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Loading similar schools */}
      {loadingSimilar && (
        <div className="similar-schools-loading">
          <div className="loading-spinner"></div>
          <p className="loading-text">Finding similar universities you might like...</p>
        </div>
      )}

      {/* Similar school suggestions */}
      {!loadingSimilar && similarSchools.length > 0 && (
        <div className="similar-schools-suggestions">
          <p className="suggestions-label">Similar schools you might consider:</p>
          <div className="suggestion-chips">
            {similarSchools.map((school) => (
              <button
                key={school.id}
                type="button"
                className="suggestion-chip"
                onClick={() => addSimilarSchool(school)}
              >
                + {school.name}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default SchoolAutocomplete;
